import { ENV_HELPERS } from '@/utils/constants'
import { useEffect, useState } from 'react'

const useAddressAutocomplete = (initialQuery = '') => {
  const [searchQuery, setSearchQuery] = useState(initialQuery)
  const [addressOptions, setAddressOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const ADDRESS_STORAGE_KEY = 'saved_addresses'
  const [freeSolo, setFreeSolo] = useState(false) //freeSolo used to avoid showing "no options" text in dropdown options initially
  const max_addresses = 1000
  const embedded_key = ENV_HELPERS.SMARTY_STREET_KEY

  const fetchAddresses = async (searchQuery) => {
    if (!searchQuery) {
      setAddressOptions([])
      return
    }

    const autoCompleteUrl = `https://us-autocomplete-pro.api.smarty.com/lookup?key=${embedded_key}&source=all&prefer_geolocation=city&search=${searchQuery}`

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(autoCompleteUrl)

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const data = await response.json()
      const updatedData = data.suggestions.map((address) => ({
        street: address.street_line,
        city: address.city,
        state: address.state,
        zipcode: address.zipcode,
        entries: address.entries,
      }))

      setAddressOptions(updatedData)
      saveAddressesToLocal(updatedData)
    } catch (err) {
      console.error(err)
      setError(err.message)
      setLoading(false)
    } finally {
      setLoading(false)
      setFreeSolo(false)
    }
  }

  const saveAddressesToLocal = (newAddresses: any[]) => {
    let storedAddresses = getLocalAddresses()

    // Ensure only unique addresses are stored (using street_line as a unique identifier)
    const existingStreetLines = new Set(storedAddresses.map((addr) => addr.street))
    const uniqueNewAddresses = newAddresses.filter((addr) => !existingStreetLines.has(addr.street))

    // Append new addresses and maintain limit of max_addresses
    storedAddresses = [...storedAddresses, ...uniqueNewAddresses]
    if (storedAddresses.length > max_addresses) {
      storedAddresses = storedAddresses.slice(storedAddresses.length - max_addresses) // Keep the most recent max_addresses
    }

    localStorage.setItem(ADDRESS_STORAGE_KEY, JSON.stringify(storedAddresses))
  }

  const searchLocalAddresses = (searchQuery): any[] => {
    if (!searchQuery) {
      return JSON.parse(localStorage.getItem(ADDRESS_STORAGE_KEY) || '[]')
    }
    const allAddresses = JSON.parse(localStorage.getItem(ADDRESS_STORAGE_KEY) || '[]')
    const filteredAddresses = allAddresses.filter((address) =>
      address.street?.toLowerCase().includes(searchQuery?.toLowerCase())
    )
    setAddressOptions(filteredAddresses)
    return filteredAddresses
  }

  const getLocalAddresses = (): any[] => {
    return JSON.parse(localStorage.getItem(ADDRESS_STORAGE_KEY) || '[]')
  }

  return {
    addressOptions,
    loading,
    error,
    setSearchQuery,
    fetchAddresses,
    searchLocalAddresses,
    setLoading,
    searchQuery,
    freeSolo,
    setFreeSolo,
  }
}

export default useAddressAutocomplete
